import { z } from 'zod'

import { addAttachments, createLotoReportDraft } from './lotoReportDraft'
import type {
  LotoAttachment,
  LotoReport,
  LotoReportEntry,
  LotoReportScope,
} from './lotoReportSchemas'
import { matchesLotoScope } from './lotoReportScope'
import {
  type LotoReportSummary,
  summarizeLotoReports,
} from './lotoReportSummaries'

const pageTagsSchema = z.looseObject({
  organization: z.string().min(1),
  site: z.string().min(1),
  'pipeline-config': z.string().min(1),
  'system-document': z.string().min(1),
})

export function scopedPageAttachment(
  pageId: string,
  pageTags: Record<string, string | null> | undefined,
  scope: LotoReportScope
): LotoAttachment | undefined {
  const tags = pageTagsSchema.safeParse(pageTags).data
  if (!pageId || !tags || !matchesLotoScope(tags, scope)) return undefined
  return { system_document_id: tags['system-document'], page_ids: [pageId] }
}

export function reportHasPage(
  report: LotoReport,
  attachment: LotoAttachment
): boolean {
  return report.attachments.some(
    ({ system_document_id, page_ids }) =>
      system_document_id === attachment.system_document_id &&
      attachment.page_ids.every((pageId) => page_ids.includes(pageId))
  )
}

export function lotoPageReports(
  reports: LotoReportEntry[],
  attachment: LotoAttachment
): { onPage: LotoReportSummary[]; others: LotoReportSummary[] } {
  const onPage = reports.filter(({ data }) => reportHasPage(data, attachment))
  const others = reports.filter(({ data }) => !reportHasPage(data, attachment))
  return {
    onPage: summarizeLotoReports(onPage),
    others: summarizeLotoReports(others),
  }
}

export function newLotoDraftWithPage(
  name: string,
  scope: LotoReportScope,
  attachment: LotoAttachment,
  retryDraft?: LotoReportEntry
): LotoReportEntry {
  const entry =
    retryDraft?.data.name === name && matchesLotoScope(retryDraft.tags, scope)
      ? retryDraft
      : createLotoReportDraft(scope, name)
  return { ...entry, data: addAttachments(entry.data, [attachment]) }
}
